import React, { useState, useEffect } from 'react';
import { companyService, countryService, stateService, districtService } from '../../services/api';
import './AdminPage.css';

function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    Name: '',
    Code: '',
    Password: '',
    MobileNo: '',
    Email: '',
    Address: '',
    Location: '',
    Description: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    Logo: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      loadStates();
    }
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      loadDistricts();
    }
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      alert('Error loading companies: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const data = await countryService.getCountries();
      setCountries(data);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const loadStates = async () => {
    try {
      const data = await stateService.getStates();
      setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)));
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const data = await districtService.getDistricts();
      setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleAdd = () => {
    setEditingCompany(null);
    setFormData({
      Name: '',
      Code: '',
      Password: '',
      MobileNo: '',
      Email: '',
      Address: '',
      Location: '',
      Description: '',
      CountryId: '',
      StateId: '',
      DistrictId: '',
      Logo: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      Id: company.Id,
      Name: company.Name || '',
      Code: company.Code || '',
      Password: '',
      MobileNo: company.MobileNo || '',
      Email: company.Email || '',
      Address: company.Address || '',
      Location: company.Location || '',
      Description: company.Description || '',
      CountryId: company.CountryId || '',
      StateId: company.StateId || '',
      DistrictId: company.DistrictId || '',
      Logo: company.Logo || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await companyService.deleteCompany(id);
        loadData();
      } catch (error) {
        alert('Error deleting company: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await companyService.saveCompany(formData);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving company: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, Logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Companies Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add Company</button>
      </div>

      {loading && !companies.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.Id}>
                  <td>{company.Name}</td>
                  <td>{company.Code}</td>
                  <td>{company.Email}</td>
                  <td>{company.MobileNo}</td>
                  <td>{company.Location}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(company)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(company.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCompany ? 'Edit Company' : 'Add Company'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.Code}
                    onChange={(e) => setFormData({ ...formData, Code: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password {editingCompany ? '' : '*'}</label>
                  <input
                    type="password"
                    value={formData.Password}
                    onChange={(e) => setFormData({ ...formData, Password: e.target.value })}
                    required={!editingCompany}
                    placeholder={editingCompany ? "Leave blank to keep current password" : ""}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.Email}
                    onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Mobile No</label>
                  <input
                    type="text"
                    value={formData.MobileNo}
                    onChange={(e) => setFormData({ ...formData, MobileNo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.Address}
                    onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.Location}
                    onChange={(e) => setFormData({ ...formData, Location: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.CountryId}
                    onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.Id} value={country.Id}>{country.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.StateId}
                    onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })}
                    disabled={!formData.CountryId}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.Id} value={state.Id}>{state.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.DistrictId}
                    onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })}
                    disabled={!formData.StateId}
                  >
                    <option value="">Select District</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.Description}
                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {formData.Logo && (
                    <img src={formData.Logo} alt="Logo preview" className="image-preview" />
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCompanies;

